import React, { FC, useCallback, useEffect, useState } from 'react';
import { Tooltip } from '../../shared/components';
import { useUser } from '../../shared';
import { BlockType, BlockWithNotes } from '../../../config/api/models/Block';
import * as Styles from './Notes.styles';
import { BlockNote } from 'src/config/api/models/BlockNote';
import { TeacherFiltersEnum } from 'src/modules/shared/constants';
import { Exercise, ExerciseType } from 'src/config/api/models/Exercise';
import { Typography, useTheme } from '@material-ui/core';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { FontAwesomeSpriteButton } from 'src/modules/shared/components/FontAwesomeLocal';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import {
  SidePanelModes,
  changePanelContent,
  openSidePanel,
} from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import { setCurrentBlockId } from 'src/modules/shared/slices/trajectories/entity/trajectorySlice';
import {
  getBlockNotes,
  useCreateBlockNoteMutation,
  usePatchBlockNoteMutation,
} from 'src/modules/shared/slices/notes/api/notes';
import {
  resetCurrentEditingNote,
  setBlockNotes,
  setCurrentEditingNote,
} from 'src/modules/shared/slices/notes/entity/BlockNotesSlice';
import ConfirmationModal from './ConfirmationModal';
import { scrollToViewById } from 'src/modules/shared/helpers/scroll';

type NotesIndicatorProps = {
  pageId: string;
  blockType?: BlockType | ExerciseType;
  feedbackEnabled: boolean;
  bookmarkEnabled: boolean;
  className?: string;
  block: BlockWithNotes | Exercise;
  note: BlockNote | undefined;
};

export type ChangeNotesModal = {
  showModal: boolean;
  currentBlockId?: null | string;
  newBlockId?: null | string;
  noteToChangeId?: undefined | string;
  title?: string;
  okButtonText: string;
  handleOk: () => void;
  cancelButtonText?: string;
  handleCancel: () => void;
};

const NotesIndicator: FC<NotesIndicatorProps> = ({
  pageId,
  block,
  feedbackEnabled,
  bookmarkEnabled,
  className,
  note,
}) => {
  const { isTeacher, isStudent, isAuthor } = useUser();
  const dispatch = useAppDispatch();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const studentMode = useAppSelector((state) => state.blockNotes.studentMode);
  const currentEditingNote = useAppSelector((state) => state.blockNotes.currentEditingNote);
  const isSidePanelOpen = useAppSelector((state) => state.sidepanel.isOpen);

  const [editingNote, setEditingNote] = useState<string | undefined>(undefined);
  const [changeNotesModal, setChangeNotesModal] = useState<ChangeNotesModal>({
    showModal: false,
    okButtonText: '',
    handleOk: () => {},
    handleCancel: () => {},
  });

  const theme = useTheme();
  const log = useDataLogging();
  const { bookId, trajectoryId, chapterId } = useParams<UrlParams>();

  const [createBlockNote] = useCreateBlockNoteMutation();
  const {
    data: notes,
    refetch: refetchBlockNotes,
    isSuccess,
  } = getBlockNotes({ pageId }, { skip: isAuthor || !pageId });
  const [patchNote, patchNoteResponse] = usePatchBlockNoteMutation({ fixedCacheKey: 'patchNote' });

  useEffect(() => {
    if (isSuccess) {
      dispatch(setBlockNotes(notes));
    }
  }, [notes, dispatch, isSuccess]);

  useEffect(() => {
    if (!isSidePanelOpen) {
      dispatch(resetCurrentEditingNote());
    }
  }, [dispatch, isSidePanelOpen]);

  const adjustIndicatorPosition =
    isStudent || (isTeacher && TeacherFiltersEnum.FOLLOW_UP && selectedGroup !== '');

  const handleOpenSidePanelWithNotes = useCallback(
    (requestPageId, blockId, notesChanged = true) => {
      // if it has an editing note open
      if (
        blockId !== currentEditingNote.selectedNoteBlockId &&
        currentEditingNote.isEditing &&
        currentEditingNote?.selectedNoteBlockId &&
        notesChanged
      ) {
        // check if has any changes on content and save if wanted
        if (currentEditingNote.hasChangedContent) {
          return setChangeNotesModal({
            showModal: true,
            currentBlockId: currentEditingNote.selectedNoteBlockId,
            noteToChangeId: currentEditingNote.selectedNoteId,
            newBlockId: blockId,
            title: 'Wil je deze notitie bewaren en sluiten?',
            cancelButtonText: 'Annuleer',
            okButtonText: 'Bewaar en sluit',
            handleOk: () =>
              patchNote({
                studentNoteId: currentEditingNote.selectedNoteId,
                content: currentEditingNote.hasChangedContent,
              }),
            handleCancel: () => {
              setChangeNotesModal((prev) => {
                return { ...prev, showModal: false };
              });
              scrollToViewById(`trajectory_block_${currentEditingNote.selectedNoteBlockId}`);
            },
          });
        } else {
          return setChangeNotesModal({
            showModal: true,
            title: 'Wil je deze notitie sluiten?',
            okButtonText: 'Bewaar en sluiten',
            cancelButtonText: 'Annuleer',
            handleOk: () => {
              handleOpenSidePanelWithNotes(requestPageId, blockId, false);
            },
            handleCancel: () => {
              setChangeNotesModal((prev) => {
                return { ...prev, showModal: false };
              });
              scrollToViewById(`trajectory_block_${currentEditingNote.selectedNoteBlockId}`);
            },
          });
        }
      }
      setChangeNotesModal((prev) => {
        return { ...prev, showModal: false };
      });
      dispatch(setCurrentEditingNote({ selectedNoteBlockId: blockId }));

      if (studentMode) return null;
      if (!note && !studentMode) {
        return createBlockNote({ pageId: requestPageId, content: '', blockId })
          .then(() => {
            log(LogActionTypes.CREATE_NOTE, {
              bookId,
              chapterId,
              trajectoryId,
              blockId: blockId,
            });
          })
          .then(() => {
            refetchBlockNotes()
              .then((res) => {
                let selectedNoteId = '';
                if (res && res.data && res?.data?.length > 0) {
                  selectedNoteId = res?.data.filter((item) => item.blockId === blockId)[0].id;
                }
                dispatch(
                  setCurrentEditingNote({
                    selectedNoteBlockId: blockId,
                    isEditing: true,
                    selectedNoteId,
                  })
                );
                dispatch(changePanelContent(SidePanelModes.NOTES));
                dispatch(openSidePanel(true));
                dispatch(setCurrentBlockId(blockId));
                dispatch(setBlockNotes(res.data as BlockNote[]));
              })
              .catch((error) => console.log('i am error'));
          });
      }
      if (note && blockId !== editingNote) {
        setEditingNote(blockId);
        dispatch(setCurrentEditingNote({ selectedNoteBlockId: blockId }));
      }

      refetchBlockNotes()
        .then((res) => {
          dispatch(changePanelContent(SidePanelModes.NOTES));
          dispatch(openSidePanel(true));
          dispatch(setCurrentBlockId(blockId));
          dispatch(setCurrentEditingNote({ selectedNoteBlockId: blockId }));
          dispatch(setBlockNotes(res.data as BlockNote[]));
        })
        .then(() => {
          log(LogActionTypes.SHOW_NOTE, {
            bookId,
            chapterId,
            trajectoryId,
            blockId: blockId,
          });
        })
        .catch((error) => console.log('i am error'));
    },
    [
      currentEditingNote,
      dispatch,
      studentMode,
      note,
      editingNote,
      refetchBlockNotes,
      patchNote,
      createBlockNote,
      log,
      bookId,
      chapterId,
      trajectoryId,
    ]
  );

  useEffect(() => {
    if (patchNoteResponse.isSuccess && changeNotesModal.currentBlockId) {
      dispatch(resetCurrentEditingNote());
      handleOpenSidePanelWithNotes(pageId, changeNotesModal.newBlockId, false);
      patchNoteResponse.reset();
    }
  }, [
    changeNotesModal.currentBlockId,
    changeNotesModal.newBlockId,
    dispatch,
    handleOpenSidePanelWithNotes,
    pageId,
    patchNoteResponse,
    patchNoteResponse.isSuccess,
  ]);

  if (block.type === 'wysiwyg' && block.pageType?.includes('exercise')) {
    return null;
  }

  return (
    <Styles.NotesIndicatorWrapper
      blockType={block.type}
      feedbackEnabled={feedbackEnabled}
      bookmarkEnabled={bookmarkEnabled}
      className={className}
      adjustIndicatorPosition={!adjustIndicatorPosition}
    >
      <Tooltip
        placement="left"
        title={
          <>
            <Typography variant="subtitle1">
              {`${note ? 'Notitie bekijken: ' : 'Voeg een notitie toe'}`}
            </Typography>
          </>
        }
      >
        <button
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            zIndex: 333,
            backgroundColor: 'transparent',
            outline: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          onClick={() => handleOpenSidePanelWithNotes(pageId, block.id)}
        ></button>
      </Tooltip>
      <FontAwesomeSpriteButton
        icon={SpriteIconsLight.NOTEBOOK}
        color={
          notes?.some((item) => item.blockId === block.id)
            ? theme.palette.common.white
            : theme.palette.grey.A100
        }
        iconSize="md"
        onClick={() => {}}
      />

      <ConfirmationModal modal={changeNotesModal} />
    </Styles.NotesIndicatorWrapper>
  );
};

export default NotesIndicator;
